<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import type { Component } from 'vue'

import type { Icons } from '@/components/base/BaseIcon.vue'
import { cn } from '@/ui/lib/utils'

interface TemplateCardProps {
  buttonText?: string
  category?: string
  description?: string
  icon: Icons | Component
  isRecommended?: boolean
  isSelected?: boolean
  processing?: boolean
  processingLabel?: string
  recommendedText?: string
  showTime?: boolean
  timeUnit?: string
  timeValue?: string | number
  title: string
}

const props = withDefaults(defineProps<TemplateCardProps>(), {
  processing: false,
  showTime: false
})

const isStringIcon = computed(() => typeof props.icon === 'string')
const iconName = computed(() => props.icon as Icons)

const emit = defineEmits<{
  (e: 'click'): void
  (e: 'action'): void
}>()

const handleClick = () => {
  emit('click')
}

const handleAction = () => {
  emit('action')
}
</script>

<template>
  <div
    :class="
      cn(
        'relative overflow-hidden rounded-xl border border-border bg-white p-4 cursor-pointer transition-[border-color,box-shadow] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[box-shadow,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border motion-reduce:transition-none duration-300',
        isSelected ? 'border-primary' : 'hover:border-stone-300 hover:shadow-card-hover'
      )
    "
    @click="handleClick"
  >
    <div class="flex flex-col h-full">
      <div class="flex flex-col gap-4 items-start flex-grow">
        <div class="w-full flex flex-row items-center justify-between">
          <div class="rounded-md bg-muted p-2">
            <BaseIcon v-if="isStringIcon" :name="iconName" class="h-5 w-5" :stroke-width="1.5" />
            <component :is="icon" v-else class="h-5 w-5" :stroke-width="1.5" />
          </div>

          <BaseChip v-if="isRecommended" :text="recommendedText" color="blue" />
        </div>
        <div class="flex flex-col gap-2 items-start flex-grow">
          <p class="text-sm text-foreground font-medium">{{ title }}</p>
          <p v-if="description" class="text-sm font-normal text-foreground-secondary">
            {{ description }}
          </p>
        </div>
      </div>
      <slot name="preview" />
      <div v-if="buttonText || showTime || $slots.default" class="w-full flex flex-row items-center gap-4 mt-4">
        <Button
          v-if="buttonText"
          type="button"
          :variant="isSelected ? 'default-primary' : 'outline'"
          :class="!isSelected && 'bg-white text-foreground border-border hover:bg-accent'"
          :disabled="processing"
          @click.stop="handleAction"
        >
          <Loader2 v-if="processing" class="animate-spin" />
          {{ processing && processingLabel ? processingLabel : buttonText }}
        </Button>

        <div v-if="showTime" class="flex flex-row items-center gap-2">
          <BaseIcon name="IconoirAlarm" class="h-4 w-4" :stroke-width="1.5" />
          <p class="text-sm font-normal text-foreground-secondary">{{ timeValue }} {{ timeUnit }}</p>
        </div>

        <slot />
      </div>
    </div>
  </div>
</template>
